import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useRouterAdapter } from '../router-adapter';

interface NavigationProgressContextProps {
  isNavigating: boolean;
  startNavigation: () => void;
}

const NavigationProgressContext =
  createContext<NavigationProgressContextProps | null>(null);

const MIN_PROGRESS_DURATION = 300; // Minimum time to show progress bar (ms)

export function NavigationProgressProvider({ children }: PropsWithChildren) {
  const [isNavigating, setIsNavigating] = useState(false);
  const { pathname, searchParams } = useRouterAdapter();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const minTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevPathnameRef = useRef(pathname);
  const navigationStartRef = useRef<number | null>(null);

  const startNavigation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (minTimeoutRef.current) {
      clearTimeout(minTimeoutRef.current);
    }
    setIsNavigating(true);
    navigationStartRef.current = Date.now();

    // Safety timeout - hide after 10 seconds max
    timeoutRef.current = setTimeout(() => {
      setIsNavigating(false);
      navigationStartRef.current = null;
    }, 10000);
  }, []);

  const completeNavigation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Ensure minimum display time for visibility
    const elapsed = navigationStartRef.current
      ? Date.now() - navigationStartRef.current
      : MIN_PROGRESS_DURATION;
    const remainingTime = Math.max(0, MIN_PROGRESS_DURATION - elapsed);

    if (remainingTime > 0) {
      minTimeoutRef.current = setTimeout(() => {
        setIsNavigating(false);
        navigationStartRef.current = null;
      }, remainingTime);
    } else {
      setIsNavigating(false);
      navigationStartRef.current = null;
    }
  }, []);

  // Detect route changes to complete navigation
  useEffect(() => {
    if (prevPathnameRef.current !== pathname) {
      prevPathnameRef.current = pathname;
      completeNavigation();
    }
  }, [pathname, searchParams, completeNavigation]);

  // Document-level click listener to catch all Link clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      if (!link) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:')) return;

      // Check if it's a same-origin navigation
      try {
        const url = new URL(href, window.location.origin);

        // Only trigger for same-origin, different-path navigations
        if (
          url.origin === window.location.origin &&
          url.pathname !== pathname
        ) {
          startNavigation();
        }
      } catch {
        // Invalid URL, ignore
      }
    };

    // Use capture phase to catch clicks before they might be stopped by other handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [pathname, startNavigation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <NavigationProgressContext.Provider
      value={{ isNavigating, startNavigation }}
    >
      {children}
    </NavigationProgressContext.Provider>
  );
}

export function useNavigationProgress(): NavigationProgressContextProps {
  const context = useContext(NavigationProgressContext);
  if (!context) {
    throw new Error(
      'useNavigationProgress must be used within NavigationProgressProvider',
    );
  }
  return context;
}
